<template>
	<section v-loading="loading" class="statistics-library">
		<header class="library-header">
			<div><span class="eyebrow">工作台配置</span><h2>统计内容库</h2><p>按业务选择指标，配置展示方式，再添加到首页模板。</p></div>
			<div class="header-actions"><el-button icon="el-icon-refresh" @click="load">刷新</el-button><el-button v-if="canEdit" :loading="installing" @click="installStaff">添加人员统计预设</el-button><el-button v-if="canEdit" type="primary" icon="el-icon-plus" @click="edit()">新增统计内容</el-button></div>
		</header>
		<div class="library-overview"><div><span>已配置统计</span><strong>{{ rows.length }}</strong></div><div><span>启用中</span><strong>{{ rows.filter(w => w.enabled).length }}</strong></div><div><span>可用指标</span><strong>{{ metrics.length }}</strong></div><div><span>业务分类</span><strong>{{ categories.length }}</strong></div></div>
		<el-alert v-if="loadError" :title="loadError" type="error" :closable="false" />
		<div class="library-body">
			<aside class="category-nav" aria-label="统计分类"><h4>业务分类</h4><button :class="{active: !category}" @click="category = ''">全部内容 <span>{{ libraryItems.length }}</span></button><button v-for="c in categories" :key="c" :class="{active: category === c}" @click="category = c">{{ c }} <span>{{ categoryCount(c) }}</span></button></aside>
			<main class="library-main">
				<lcsj-filter :show-search="false">
					<el-form-item label="内容："><el-select v-model="libraryMode" size="small"><el-option label="已配置统计" value="saved" /><el-option label="指标目录" value="catalog" /></el-select></el-form-item>
					<el-form-item label="关键词："><el-input v-model="search" size="small" placeholder="名称、编码或统计指标" prefix-icon="el-icon-search" clearable /></el-form-item>
					<el-form-item label="展示类型："><el-select v-model="displayFilter" size="small" clearable placeholder="全部类型"><el-option v-for="(label, key) in types" :key="key" :label="label" :value="key" /></el-select></el-form-item>
					<el-form-item v-if="libraryMode === 'saved'" label="状态："><el-select v-model="statusFilter" size="small" clearable placeholder="全部状态"><el-option label="启用" value="enabled" /><el-option label="停用" value="disabled" /></el-select></el-form-item>
				</lcsj-filter>
				<div class="results-heading"><strong>{{ category || '全部内容' }}</strong><span>{{ filtered.length }} 项 · 卡片图形仅为样式示意</span></div>
				<div class="statistic-cards">
					<article v-for="w in pageItems" :key="w.keyId" class="statistic-card" :class="{disabled: !w.enabled}" :style="{'--card-accent': w.color}">
						<div class="statistic-card-heading"><span class="type-icon"><i :class="typeIcon(w.displayType)" /></span><el-tag size="mini" :type="w.enabled ? 'success' : 'info'">{{ w.isCatalog ? '可配置' : w.enabled ? '启用' : '停用' }}</el-tag></div>
						<h3>{{ w.name }}</h3><p class="metric-code">{{ w.code }}</p><p class="metric-description">{{ metric(w.metric).description }}</p>
						<div class="card-example" aria-hidden="true"><strong v-if="w.displayType === 'number'">— <small>{{ w.unit }}</small></strong><div v-else-if="w.displayType === 'list'" class="mini-list"><span v-for="n in 3" :key="n"></span></div><div v-else class="mini-chart" :class="{horizontal: w.displayType === 'horizontalBar'}"><i v-for="(n, i) in [38,65,48,86,72,98]" :key="i" :style="w.displayType === 'horizontalBar' ? {width:n+'%'} : {height:n+'%'}"></i></div><span>{{ types[w.displayType] }}</span></div>
						<div class="card-tags"><el-tag size="mini" type="info">{{ metric(w.metric).category }}</el-tag><el-tag size="mini" type="info">{{ ranges[w.range] }}</el-tag><el-tag v-if="metric(w.metric).personnel" size="mini" type="info">{{ scopeNames[w.dataScope || 'authorized'] }}</el-tag><el-tag v-if="metric(w.metric).money" size="mini" type="warning">金额权限</el-tag></div>
						<footer><el-button v-if="w.isCatalog && canEdit" type="text" @click="configureMetric(w.metric)">配置此指标 <i class="el-icon-arrow-right" /></el-button><el-button v-else-if="!w.isCatalog" type="text" @click="edit(w)">{{ canEdit ? '编辑' : '查看' }}</el-button><el-button v-if="!w.isCatalog && canEdit" type="text" class="delete-widget" @click="remove(w)">删除</el-button><span v-if="w.isCatalog && !canEdit" class="hint">需统计内容维护权限</span></footer>
					</article>
				</div>
				<el-empty v-if="!filtered.length && !loading && !loadError" description="暂无匹配内容，可调整筛选或从指标目录创建" :image-size="80" />
				<el-pagination v-if="filtered.length > 12" class="library-pagination" :current-page.sync="page" :page-size="12" :total="filtered.length" layout="prev, pager, next" />
				<p class="scope-note">统计口径：账号启停不代表入离职；尚未打卡不代表旷工；经办金额不代表个人业绩；工资确认不代表实际付款。迟到、早退、离职率和业绩分摊需补充业务规则与记录后开放。</p>
			</main>
		</div>
		<el-drawer title="维护统计内容" :visible.sync="visible" size="min(640px, 100vw)" append-to-body :wrapper-closable="false" :before-close="close">
			<div class="widget-form">
				<el-form :model="form" label-position="top" size="small" :disabled="!canEdit || saving">
					<h4>基本信息</h4>
					<el-form-item label="名称" required><el-input v-model="form.name" maxlength="50" /></el-form-item>
					<el-form-item label="编码" required><el-input v-model="form.code" maxlength="50" placeholder="英文、数字、下划线" /></el-form-item>
					<h4>统计配置</h4>
					<el-form-item label="统计指标" required>
						<el-select v-model="form.metric" @change="changeMetric">
							<el-option-group v-for="c in categories" :key="c" :label="c">
								<el-option v-for="m in metrics.filter((x) => x.category === c)" :key="m.code" :label="m.name" :value="m.code" />
							</el-option-group>
						</el-select>
						<p class="hint">{{ metric(form.metric).description }}</p>
					</el-form-item>
					<el-form-item label="展示类型">
						<el-radio-group v-model="form.displayType">
							<el-radio-button v-for="t in metric(form.metric).types" :key="t" :label="t">{{ types[t] }}</el-radio-button>
						</el-radio-group>
						<p v-if="form.displayType === 'list'" class="hint">按指标维度展示明细，支持在块体内滚动查看。</p>
						<p v-if="form.displayType === 'horizontalBar'" class="hint">分类作为纵轴，数值作为横轴；分类较多时可拖动右侧滚动条查看。</p>
					</el-form-item>
					<el-form-item v-if="metric(form.metric).personnel" label="统计范围"><el-select v-model="form.dataScope"><el-option v-for="(label,key) in scopeNames" :key="key" :label="label" :value="key" /></el-select><p class="hint">范围始终受当前登录用户的门店权限限制；本人取当前登录人。</p></el-form-item>
					<el-form-item label="默认时间范围">
						<el-select v-model="form.range"><el-option v-for="r in metric(form.metric).ranges" :key="r" :label="ranges[r]" :value="r" /></el-select>
					</el-form-item>
					<h4>展示与权限</h4>
					<div class="pair">
						<el-form-item label="单位"><el-input v-model="form.unit" maxlength="10" /></el-form-item>
						<el-form-item label="小数位"><el-input-number v-model="form.decimals" :min="0" :max="4" /></el-form-item>
					</div>
					<div class="pair">
						<el-form-item label="主题颜色"><el-color-picker v-model="form.color" /></el-form-item>
						<el-form-item label="启用状态"><el-switch v-model="form.enabled" /></el-form-item>
					</div>
					<el-form-item label="附加查看权限（可选）">
						<el-select v-model="form.permissionKeyId" filterable clearable placeholder="在业务权限基础上增加限制">
							<el-option v-for="p in permissionOptions" :key="p.keyId" :label="p.menuName" :value="p.keyId" />
						</el-select>
						<p class="hint">始终要求对应业务菜单权限。{{ metric(form.metric).money ? "金额指标还要求对应业务的首页金额查看权限。" : "" }}</p>
					</el-form-item>
				</el-form>
				<div class="hint">展示预览 · 示例数据</div>
				<home-widget :widget="form" :placement="{ height: form.displayType === 'number' ? 140 : 260 }" :value="example(form)" />
				<el-alert title="停用立即生效；其他展示修改需在模板中重新保存草稿并发布。" type="info" :closable="false" />
			</div>
			<div class="drawer-footer">
				<el-button @click="close(() => (visible = false))">取消</el-button>
				<el-button v-if="canEdit" type="primary" :loading="saving" @click="save">保存</el-button>
			</div>
		</el-drawer>
	</section>
</template>
<script>
import { homePageRequest as api } from "@/api/homePageApi";
import { rangeNames, typeNames, exampleValue, scopeNames } from "@/utils/homeLayout";
import HomeWidget from "@/views/mainIndex/homeWidget.vue";
export default {
	components: { HomeWidget },
	props: { permissions: Array },
	data: () => ({
		loading: false,
		installing: false, loadError: "", libraryMode: "saved", displayFilter: "", statusFilter: "", page: 1, scopeNames,
		saving: false,
		rows: [],
		metrics: [],
		permissionOptions: [],
		search: "",
		category: "",
		visible: false,
		form: {},
		keyId: null,
		revision: 0,
		baseline: "",
		ranges: rangeNames,
		types: typeNames
	}),
	computed: {
		canEdit() {
			return this.permissions.includes("homeWidgetEdit");
		},
		categories() {
			return [...new Set(this.metrics.map((m) => m.category))];
		},
		libraryItems() {
			return this.libraryMode === 'saved' ? this.rows : this.metrics.map(m => ({keyId:'metric:'+m.code, isCatalog:true, name:m.name, code:m.code, metric:m.code, displayType:m.types[0], range:m.ranges.includes('month') ? 'month' : m.ranges[0], color:'#0e7c66', unit:m.unit || (m.money ? '元' : '单'), enabled:true, dataScope:'authorized'}));
		},
		filtered() { const q=this.search.trim().toLowerCase(); return this.libraryItems.filter(w => [w.name,w.code,this.metric(w.metric).name].some(s => (s || '').toLowerCase().includes(q)) && (!this.category || this.metric(w.metric).category === this.category) && (!this.displayFilter || (w.isCatalog ? this.metric(w.metric).types.includes(this.displayFilter) : w.displayType === this.displayFilter)) && (this.libraryMode === 'catalog' || !this.statusFilter || w.enabled === (this.statusFilter === 'enabled'))); },
		pageItems() { return this.filtered.slice((this.page-1)*12, this.page*12); }

	},
	watch: { search() { this.page=1; }, category() { this.page=1; }, libraryMode() { this.page=1; }, displayFilter() { this.page=1; }, statusFilter() { this.page=1; } },
	created() {
		this.load();
	},
	methods: {
		example: exampleValue,
		categoryCount(c) { return this.libraryItems.filter(w => this.metric(w.metric).category === c).length; },
		typeIcon(t) { return {number:'el-icon-data-line',line:'el-icon-data-analysis',bar:'el-icon-s-data',horizontalBar:'el-icon-s-operation',list:'el-icon-tickets'}[t] || 'el-icon-data-analysis'; },
		async installStaff() { if (this.installing || !this.canEdit) return; this.installing=true; try { const count=await api('InstallStaffWidgets'); this.$message.success('已添加 '+count+' 项人员统计，已有配置保持不变'); this.libraryMode='saved'; await this.load(); } catch(e) { this.$message.error(e.message); } finally { this.installing=false; } },
		configureMetric(code) { this.edit(); this.form.metric=code; this.changeMetric(); this.form.name=this.metric(code).name; this.form.code='staff_'+code.replace(/\./g,'_'); this.baseline=JSON.stringify(this.form); },
		metric(code) {
			return this.metrics.find((m) => m.code === code) || { types: [], ranges: [] };
		},
		async load() {
			this.loading = true; this.loadError="";
			try {
				const [rows, c] = await Promise.all([api("Widgets"), api("Catalog")]);
				this.rows = rows; this.page = 1;
				this.metrics = c.metrics;
				this.permissionOptions = c.permissions;
			} catch (e) {
				this.loadError=e.message;
			} finally {
				this.loading = false;
			}
		},
		edit(row) {
			this.keyId = row ? row.keyId : null;
			this.revision = row ? row.revision : 0;
			this.form = row
				? { dataScope: "authorized", ...row }
				: { name: "", code: "", dataScope: "authorized", metric: "process.count", displayType: "number", range: "today", unit: "单", color: "#0e7c66", decimals: 0, enabled: true, permissionKeyId: null };
			this.baseline = JSON.stringify(this.form);
			this.visible = true;
		},
		changeMetric() {
			const m = this.metric(this.form.metric);
			this.form.displayType = m.types[0];
			this.form.range = m.ranges[0];
			this.form.unit = m.unit != null ? m.unit : m.money ? "元" : m.category === "顾客统计" ? "人" : "单";
			this.form.dimension = m.dimension || "日期";
			if (!m.personnel) this.form.dataScope = "authorized";
			this.form.decimals = m.money || ["%", "天"].includes(m.unit) ? 2 : 0;
		},
		async save() {
			if (this.saving) return;
			this.saving = true;
			try {
				await api("SaveWidget", { keyId: this.keyId, revision: this.revision, widget: this.form });
				this.visible = false;
				this.$message.success("统计内容已保存");
				await this.load();
			} catch (e) {
				this.$message.error(e.message);
			} finally {
				this.saving = false;
			}
		},
		close(done) {
			if (this.saving) return;
			if (!this.canEdit || this.baseline === JSON.stringify(this.form)) return done();
			this.$confirm("有未保存修改，确定关闭？", "提示")
				.then(() => done())
				.catch(() => {});
		},
		async remove(row) {
			try {
				await this.$confirm(`确定删除“${row.name}”？`, "删除统计内容");
			} catch (_) {
				return;
			}
			try {
				await api("DeleteWidget", { keyId: row.keyId, revision: row.revision });
				this.$message.success("已删除");
				await this.load();
			} catch (e) {
				this.$message.error(e.message);
			}
		}
	}
};
</script>
<style scoped>
.statistics-library { padding: 8px 4px 20px; color: var(--app-text); }
.library-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; padding:20px 24px; border:1px solid var(--app-border); border-radius:12px; background:var(--app-surface); }
.eyebrow { font-size:12px; color:var(--app-primary); letter-spacing:2px; }
.library-header h2 { margin:8px 0; font-size:24px; }
.library-header p { margin:0; color:var(--app-text-secondary); font-size:13px; }
.header-actions { display:flex; flex-wrap:wrap; gap:8px; }
.header-actions .el-button { margin-left:0; }
.library-overview { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:16px; margin:16px 0 24px; }
.library-overview > div { padding:18px 22px; border:1px solid var(--app-border); border-radius:10px; background:var(--app-surface); display:flex; justify-content:space-between; align-items:center; }
.library-overview span { color:var(--app-text-secondary); font-size:13px; }
.library-overview strong { font-size:27px; font-variant-numeric:tabular-nums; }
.library-body { display:grid; grid-template-columns:190px minmax(0,1fr); gap:24px; }
.category-nav { align-self:start; padding:16px 10px; background:var(--app-surface); border:1px solid var(--app-border); border-radius:10px; }
.category-nav h4 { margin:0 10px 12px; font-size:13px; color:var(--app-text-secondary); }
.category-nav button { display:flex; justify-content:space-between; width:100%; border:0; border-radius:6px; background:transparent; color:inherit; padding:11px 10px; cursor:pointer; text-align:left; margin:2px 0; }
.category-nav button.active { background:var(--app-surface-muted); color:var(--app-primary); font-weight:600; box-shadow:inset 3px 0 var(--app-primary); }
.category-nav button:hover { background:var(--app-surface-muted); }
.category-nav button:focus-visible { outline:2px solid var(--app-primary); outline-offset:1px; }
.category-nav span { color:var(--app-text-secondary); font-size:12px; }
.library-main { min-width:0; }
.library-filters { display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
.library-filters > .el-input { width:240px; }
.library-filters > .el-select { width:150px; }
.results-heading { display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; margin:22px 0 14px; }
.results-heading span { font-size:12px; color:var(--app-text-secondary); }
.statistic-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(min(280px,100%),1fr)); gap:16px; }
.statistic-card { min-width:0; display:flex; flex-direction:column; padding:20px; background:var(--app-surface); border:1px solid var(--app-border); border-radius:10px; transition:border-color .15s,box-shadow .15s; }
.statistic-card:hover { border-color:var(--app-primary); box-shadow:0 4px 16px rgba(0,0,0,.04); }
.statistic-card.disabled { opacity:.7; }
.statistic-card-heading { display:flex; align-items:center; justify-content:space-between; }
.type-icon { width:36px; height:36px; display:grid; place-items:center; border-radius:9px; background:var(--app-surface-muted); color:var(--card-accent); font-size:20px; }
.statistic-card h3 { margin:16px 0 6px; font-size:16px; }
.metric-code { margin:0 0 10px; font-size:11px; color:var(--app-text-secondary); overflow-wrap:anywhere; }
.metric-description { font-size:12px; line-height:1.7; color:var(--app-text-secondary); margin:0 0 16px; min-height:60px; }
.card-example { margin-top:auto; height:75px; border-radius:6px; background:var(--app-surface-muted); padding:12px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
.card-example > span { font-size:12px; color:var(--app-text-secondary); white-space:nowrap; }
.card-example strong { font-size:32px; color:var(--card-accent); }
.card-example small { font-size:12px; font-weight:400; }
.mini-chart { display:flex; align-items:flex-end; gap:6px; height:48px; width:120px; }
.mini-chart i { background:var(--card-accent); width:12px; border-radius:2px; opacity:.65; }
.mini-chart.horizontal { flex-direction:column; align-items:flex-start; gap:3px; }
.mini-chart.horizontal i { height:5px; }
.mini-list { width:120px; display:grid; gap:9px; }
.mini-list span { height:6px; background:var(--app-border); border-radius:2px; }
.mini-list span:nth-child(2) { width:80%; }
.card-tags { display:flex; flex-wrap:wrap; gap:6px; margin:14px 0; }
.statistic-card footer { display:flex; justify-content:space-between; border-top:1px solid var(--app-border); padding-top:6px; }
.delete-widget { color:var(--app-text-secondary); }
.library-pagination { margin-top:24px; text-align:right; }
.scope-note { font-size:12px; color:var(--app-text-secondary); line-height:1.8; padding-top:20px; }
@media(max-width:850px) { .library-body { grid-template-columns:1fr; gap:16px; } .category-nav { display:flex; overflow:auto; gap:8px; } .category-nav h4 { display:none; } .category-nav button { width:auto; flex-shrink:0; gap:10px; } .library-overview { gap:8px; grid-template-columns:repeat(2,minmax(0,1fr)); } .library-header { padding:16px; } }
.widget-form > .home-widget { height: auto; }
.toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
	margin: 12px 0 20px;
}
.toolbar .el-input {
	width: 250px;
}
.toolbar .el-select {
	width: 150px;
}
.widget-form {
	padding: 0 24px 85px;
	height: calc(100vh - 85px);
	overflow: auto;
	box-sizing: border-box;
}
.widget-form h4 {
	border-left: 3px solid var(--app-primary);
	padding-left: 8px;
	margin-top: 20px;
}
.widget-form .el-select {
	width: 100%;
}
.pair {
	display: flex;
	gap: 20px;
}
.pair > * {
	flex: 1;
	min-width: 0;
}
.hint {
	font-size: 12px;
	color: var(--app-text-secondary);
	line-height: 1.7;
}
.drawer-footer {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 14px 24px;
	text-align: right;
	background: var(--app-surface);
	border-top: 1px solid var(--app-border);
}
.el-alert {
	margin-top: 14px;
}
</style>
